import { Injectable } from '@angular/core';
import { BaseEntityService } from '../core-services/base-entity.service';
import { AppointmentEntity, AppointmentReducedBaseTrait, AppointmentStatus, AppointmentTrait, PaymentStatus } from './appointment.entity';
import { AppointmentDayEntity, AppointmentDayTrait } from './appointment-day.entity';
import { genId, genVr } from '../../core/utils';
import { AppointmentDayService } from './appointment-day.service';
import { DatabaseService } from '../core-services/database.service';
import { AuthService } from '../core-services/auth.service';
import { Subscription } from 'rxjs';
import { ClientCache } from '../client/client.entity';
import { CoreService } from '../core-services/core.service';
import { AnalyticsService } from '../core-services/analytics.service';
import { ObservableService } from '../core-services/observable.service';
import { OnboardingAction, OnboardingService } from '../core-services/onboarding.service';

@Injectable({
  providedIn: 'root'
})
export class AppointmentService extends BaseEntityService<AppointmentEntity, AppointmentTrait> {

  public appointments: Map<string, AppointmentEntity> = new Map();
  public clientAppointments: Map<string, Map<string, AppointmentEntity>> = new Map();
  public get sortAppointments() {
    return Array.from(this.appointments.values()).sort((a, b) => a.startTime - b.startTime);
  }
  public appointmentDays: Map<string, AppointmentDayEntity> = new Map();
  private subscription: Subscription;

  private _firstAppointmentDate: number;
  private _lastAppointmentDate: number;

  public get firstAppointmentDate() {
    return this._firstAppointmentDate;
  }

  public get lastAppointmentDate() {
    return this._lastAppointmentDate;
  }

  constructor(
    public appointmentDayService: AppointmentDayService,
    private auth: AuthService,
    public core: CoreService,
    private analytics: AnalyticsService,
    private observableService: ObservableService,
    private onboarding: OnboardingService
  ) { 
    super();
    
      this.subscription = this.auth.onAuthStateChanged$.subscribe(async () => {

        if (this.auth.isLogged) {
          await this.refresh();
          this.publishOnChange();
        } else {
          this.clear();
        }
      });
    
  }

  public async init() {
    if (!this.subscription) {
      this.subscription = this.auth.onAuthStateChanged$.subscribe(async () => {

        if (this.auth.isLogged) {
          await this.refresh();
          this.publishOnChange();
        } else {
          this.clear();
        }
      });
    }
    this.loading++;
    if (!this.initialized) {
      await this.refresh();
    }
    this.initialized = true;
    this.loading--;
  }


  public async clear() {
    this.appointments.clear();
    this.appointmentDays.clear();
  }
  public async refresh() {
    this.appointments.clear();
    this.appointmentDays.clear();
    // await this.updateAppointmentBounds();
    // await this.load(Date.now(), 'desc');
    // await this.load(Date.now(), 'asc');
    // return await this.loadByDay();
  }

  public normalize(appointment: Partial<AppointmentTrait>) {
    const data = new AppointmentEntity(
      appointment.id || genId(appointment.startTime),
      appointment.startTime,
      appointment.endTime,
      appointment.clientId,
      appointment.title,
      appointment.price,
      appointment.createdAt,
      appointment.status || AppointmentStatus.pending,
      appointment.paymentStatus || PaymentStatus.pending,
      appointment.payments || []
    );

    data.dueDate = appointment.dueDate;
    data.clientName = appointment.clientName;
    data.generateInvoice = appointment.generateInvoice;
    // data.paidAmount = appointment.paidAmount;

    return data;
  }

  public async create(data: Omit<AppointmentTrait, 'id' | 'createdAt'>) {
    this.loading++;
    try {

      if (await this.checkOverlapping(data)) {
        throw new Error("AGENDA.TIMESLOT_ALREADY_BOOKED");
      }

     return await DatabaseService.instance.withTx(async () => {
        // const appointmentDate = new Date(data.startTime);
        // const dayId = this.appointmentDayService.genId(appointmentDate);
        // let day = this.appointmentDays.get(dayId);
        // if (!day) {
        //   day = await this.appointmentDayService.find(appointmentDate);
        //   if (!day)  {
        //   //   for (const appointment of day.appointments) {
        //   //     if (appointment.startTime === data.startTime) {
        //   //       throw new Error("AGENDA.TIMESLOT_ALREADY_BOOKED");
        //   //     }
        //   //   }
        //   // } else {
        //     day = await this.appointmentDayService.create(appointmentDate, [], false);
        //   }
        // }
        const appointment = this.normalize(data);

        await appointment.persist();
        this.analytics.appointmentCreate(appointment.startTime, appointment.endTime - appointment.startTime);
        // day.appointments.push(appointment.export());
        // await day.persist();
        // this.appointmentDays.set(dayId, day);
        this.appointments.set(appointment.id, appointment);
        this.publishOnChange(appointment);
        // this.observableService.publishAppointmentCreate(appointment);
        setTimeout(async () => await this.auth.refreshToken(), 2000);
        if (this._firstAppointmentDate > appointment.startTime) {
          this._firstAppointmentDate = appointment.startTime;
        }

        if (this._lastAppointmentDate < appointment.startTime) {
          this._lastAppointmentDate = appointment.startTime;
        }

        this.onboarding.action(OnboardingAction.APPOINTMENT_CREATED);

        return appointment;
      });
    } catch(e) { 
      const permissionError = 'Missing or insufficient permissions.' == e.message;
      if (permissionError) setTimeout(() => this.observableService.publishOnSubscriptionNeeded(), 1000);

      throw e;
    } finally {
      this.loading--;
    }
  }

  public async update(data: AppointmentTrait) {
  
    this.loading++;
    try {

      if (await this.checkOverlapping(data)) {
        throw new Error("AGENDA.TIMESLOT_ALREADY_BOOKED");
      }

      return await DatabaseService.instance.withTx(async () => {
        // const appointmentDate = new Date(data.startTime);

        // const dayId = this.appointmentDayService.genId(appointmentDate);
        // let day = this.appointmentDays.get(dayId);
        // if (!day) {
        //   day = await this.appointmentDayService.find(appointmentDate);
        //   if (!day)  {
        //   //   for (const appointment of day.appointments) {
        //   //     if (appointment.startTime === data.startTime) {
        //   //       throw new Error("AGENDA.TIMESLOT_ALREADY_BOOKED");
        //   //     }
        //   //   }
        //   // } else {
        //     day = await this.appointmentDayService.create(appointmentDate, [], false);
        //   }
        // }



        const appointment = AppointmentEntity.from<AppointmentEntity>({...data});
        appointment.version = genVr();
        console.log("update:", appointment)
        await appointment.persist();
        this.analytics.appointmentUpdate(appointment.startTime, appointment.endTime - appointment.startTime);

        // day.appointments.push(appointment.export());
        // await day.persist();
        // this.appointmentDays.set(dayId, day);
        this.appointments.set(appointment.id, appointment);
        this.publishOnChange(appointment);
        this.observableService.publishAppointmentUpdate(appointment);
        return appointment;

      });

    } catch(e) { 
      throw e;
    } finally {
      this.loading--;
    }
  }

  public async delete(data: AppointmentTrait) {
    this.loading++;
    try {
      return await DatabaseService.instance.withTx(async () => {    

        const result = await AppointmentEntity.delete(data.id);
        if (result) {
          this.appointments.delete(data.id);
          this.analytics.appointmentDelete(data.startTime, data.endTime - data.startTime);

          this.publishOnChange();
          this.observableService.publishAppointmentDelete();
        }
        return result;
      });

   } catch(e) { 
     throw e;
   } finally {
     this.loading--;
   }
  }

  // public async find(id: string) {
  //   this.loading++;
  //   try {
  //   const client = await ClientEntity.load(id);
  //   return client;
  //   } finally {
  //     this.loading--;
  //   }
  // }

  // public async findByEmail(email: string) {
  //   this.loading++;
  //   try {
  //   const client = await ClientEntity.loadWhere<ClientEntity>('email', email);
  //   return client;
  //   } finally {
  //     this.loading--;
  //   }
  // }

  public async loadByDay(date: Date = new Date()) {
    this.core.startLoading();
    console.log("loadByDay", date);
    let allDaysCached = true;
    for (let i = -1; i<=7; i++) {
      const day = new Date(date);
      day.setDate(day.getDate() + i);
      const dayId = this.appointmentDayService.genId(day);
      if (!this.appointmentDays.has(dayId)) {
        allDaysCached = false;
        date = day;
        break;
      }
    }

    if (allDaysCached) {
      return this.appointments;
    }


    this.loading++;
    try {
      const _date = new Date(date);
      const _days = _date.getTime() >= this._firstAppointmentDate && _date.getTime() <= this._lastAppointmentDate ? await this.appointmentDayService.loadAll(_date) : [];
      const days = _days.reduce((acc, day) => {
        acc.set(day.id, day);
        return acc;
      }, new Map<string, AppointmentDayEntity>());
      
      const firstDayOfWeek = this.appointmentDayService.getFirstDayOfWeek(date);

      for (let i = -1; i<=15; i++) {
        const day = new Date(firstDayOfWeek);
        day.setDate(day.getDate() + i);
        const dayId = this.appointmentDayService.genId(day);
        const appointments = days.get(dayId)?.appointments || [];

        for (const appointment of appointments) {
          this.appointments.set(appointment.id, this.normalize(appointment));
        }
        this.appointmentDays.set(dayId, new AppointmentDayEntity(dayId, appointments));
      }
      return this.appointments;
    } catch(e) {
      console.log(e);
    } finally {
      this.loading--;
      this.core.stopLoading();
    }
  }

  public async loadCacheByDay(date: Date = new Date(), clientId?: string, length: number = 7) {
    this.core.startLoading()
    try {
      const firstDayOfWeek = this.appointmentDayService.getFirstDayOfWeek(date);

      const days = await this.appointmentDayService.loadRangeFromCache(firstDayOfWeek, new Date(firstDayOfWeek.getTime() + length * 24 * 60 * 60 * 1000), clientId);
     
      for (const [key, day] of days.entries()) {
        for (const appointment of day.appointments) {
          this.appointments.set(appointment.id, this.normalize(appointment));
        }
        this.appointmentDays.set(key, day);
      }

      return this.appointments;
    } finally {
      this.core.stopLoading();
    }
  }

  public async get(id: string) {
    this.loading++;
    try {
      if (this.appointments.has(id)) {
        return this.appointments.get(id);
      }
      const appointment = await AppointmentEntity.load<AppointmentEntity>(id);
      return appointment;
    } catch(e) {
      console.log(e);
    } finally {
      this.loading--;
    }
  }

  public async load(offset: number | string = Date.now(), sort: 'asc' | 'desc' = 'asc', limit: number = 10, 
   whereEQ?: {column: keyof AppointmentTrait, value: any}) {

    if ((sort == 'asc' && `${offset}` >= `${this._lastAppointmentDate}`) || (sort == 'desc' && `${offset}` <= `${this._firstAppointmentDate || 0}`) ) return [];
    // if ((sort == 'asc' && `${offset}` >= `${this._lastAppointmentDate}`) || (sort == 'desc' && `${offset}` <= `${this._firstAppointmentDate}`) ) return Array.from(this.appointments.values());
    this.loading++;
    try {
      const appointments = await AppointmentEntity.loadAllFromDatabase<AppointmentEntity>(
        AuthService.instance?.currentUser?.uid,
        {
          parentName: 'users',
          orderBy: {
            column: 'startTime',
            sort
          },
          offset,
          limit,
          whereEQ
        }
      );

      const normalizedAppointments: AppointmentEntity[] = [];
          
      for (const appointment of appointments) {
        const data = this.normalize(appointment);
        this.appointments.set(appointment.id, data);
        normalizedAppointments.push(data);
      }

      normalizedAppointments.sort((a,b) => sort == 'asc' ? a.startTime - b.startTime : b.startTime - a.startTime);
      return normalizedAppointments;
    } catch(e) {
      console.log(e);
    } finally {
      this.loading--;
    }
  }

  public generateTitle(clientName: string, appointment: AppointmentTrait) {
    // const start = new Date(appointment.startTime);
    // const end = new Date(appointment.endTime);
    // return `${clientName} - ${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    return `${clientName}`;
  }
  
  public openModal(item?: AppointmentTrait) {
    this.editingEntity = {...item};
    this.isModalOpen = true;
    this.analytics.openAppointmentModal(!!item ? 'update' : 'create');
  }

  private async updateAppointmentBounds() {
    let firstAppointmentDate = Date.now();
    let lastAppointmentDate = Date.now();
    const first = await this.appointmentDayService.getFirstAppointmentDay();
    const last = await this.appointmentDayService.getFirstAppointmentDay('desc');
    
    if (first) {
      for (const appointment of first.appointments) {
        if (appointment.startTime < firstAppointmentDate) {
          firstAppointmentDate = appointment.startTime;
        }
      }
    }

    if (last) {
      for (const appointment of last.appointments) {
        if (appointment.startTime > lastAppointmentDate) {
          lastAppointmentDate = appointment.startTime;
        }
      }
    }

    this._firstAppointmentDate = firstAppointmentDate;
    this._lastAppointmentDate = lastAppointmentDate;

  }


  private async checkOverlapping(data: Partial<AppointmentTrait>) {
    const appointmentDate = new Date(data.startTime);

    const dayCache = await DatabaseService.instance.getCache<{
      [key: string]: AppointmentReducedBaseTrait
    }>(`${ClientCache.agendaRef(data.clientId, 'day')}/${appointmentDate.getFullYear()}${(appointmentDate.getMonth() + 1).toString().padStart(2,'0')}${appointmentDate.getDate().toString().padStart(2,'0')}`);

    if (dayCache) {
      for (const appointment of Object.values(dayCache)) {
        if (appointment.id !== data.id && 
          (appointment.et >= data.startTime && appointment.et <= data.endTime
            || appointment.st >= data.startTime && appointment.st <= data.endTime)) {
          return true;
        }
      }
    }

    return false;
  }

  public closeModal() {
    this.isModalOpen = false;
    this.editingEntity = null;
    // this.analytics.closeAppointmentModal();
    this.onboarding.action(OnboardingAction.APPOINTMENT_MODAL_CLOSE);
  }
}
